import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Download, ArrowRight, Clock } from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Resources / blog listing — neutral base, single signal-green accent,
 * editorial serif headings, hairline-bordered cards. No purple. Matches Home.js.
 * -------------------------------------------------------------------------- */

const GREEN = '#2563EB';

const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
};

const featuredPost = {
    category: "Guide",
    title: "The 2025 Guide to AI Recruiting",
    excerpt: "Everything you need to know about using Large Language Models to find, screen, and close top talent without losing the human touch.",
    readTime: "12 min read",
    date: "Dec 12, 2024"
};

const posts = [
    {
        category: "Case Study",
        title: "How TechGrow Scaled to 200 Engineers",
        excerpt: "See how the unicorn startup used AutoHire to bypass agencies entirely.",
        readTime: "5 min read"
    },
    {
        category: "Tutorial",
        title: "Writing Outreach Emails That Get Opened",
        excerpt: "Data-backed templates and subject lines with >60% open rates.",
        readTime: "8 min read"
    },
    {
        category: "Industry",
        title: "The Death of the Resume Black Hole",
        excerpt: "Why automated screening is actually fairer than manual review.",
        readTime: "6 min read"
    },
    {
        category: "Product Update",
        title: "Introducing: Autonomous Scheduling",
        excerpt: "Let our AI negotiate meeting times with candidates for you.",
        readTime: "3 min read"
    }
];

function Resources() {
    return (
        <div className="min-h-screen bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
            <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">

                {/* ===== HEADER ===== */}
                <motion.div {...reveal} transition={{ duration: 0.5 }} className="mx-auto max-w-2xl text-center">
                    <div className="inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
                        Resources
                    </div>
                    <h1 className="mt-4 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white">
                        Recruiting resources
                    </h1>
                    <p className="mt-5 text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                        Insights, guides, and tools to help you build world-class teams.
                    </p>
                </motion.div>

                {/* ===== FEATURED CARD ===== */}
                <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.05 }} className="mt-16">
                    <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.12)]">
                        <div className="grid gap-8 md:grid-cols-2">
                            <div className="flex flex-col justify-center p-10">
                                <div className="inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wider" style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}>
                                    {featuredPost.category}
                                </div>
                                <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white md:text-4xl">{featuredPost.title}</h2>
                                <p className="mt-4 text-lg leading-relaxed text-gray-600 dark:text-gray-300">{featuredPost.excerpt}</p>
                                <div className="mt-6 flex items-center gap-4 text-sm text-gray-500">
                                    <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {featuredPost.readTime}</span>
                                    <span>{featuredPost.date}</span>
                                </div>
                                <button
                                    className="mt-8 inline-flex w-fit items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white transition-colors"
                                    style={{ background: GREEN }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}
                                >
                                    <BookOpen size={18} strokeWidth={1.8} /> Read guide
                                </button>
                            </div>
                            <div className="relative flex min-h-[300px] items-center justify-center border-t border-gray-200 bg-[#F8FAFC] p-8 dark:border-gray-800 dark:bg-gray-950 md:min-h-full md:border-l md:border-t-0">
                                {/* Editorial book-cover placeholder — neutral with single green accent */}
                                <div className="flex aspect-[3/4] w-48 transform flex-col justify-between rounded-r-xl rounded-l-sm border-l-4 border-gray-200 bg-white p-5 shadow-[0_24px_48px_-24px_rgba(15,23,42,0.25)] transition-transform duration-500 hover:-translate-y-1 dark:border-gray-700 dark:bg-gray-900">
                                    <div>
                                        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: GREEN }}>
                                            <span className="h-1 w-1 rounded-full" style={{ background: GREEN }} />
                                            Guide
                                        </div>
                                        <div className="mt-3 font-serif text-2xl font-semibold leading-tight text-gray-900 dark:text-white">AI Hiring</div>
                                    </div>
                                    <div className="flex h-8 w-8 items-center justify-center self-end rounded-full" style={{ background: '#EFF4FE', color: GREEN }}>
                                        <BookOpen size={15} strokeWidth={1.8} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ===== CATEGORIES NAV (visual only) ===== */}
                <div className="mt-14 flex gap-3 overflow-x-auto pb-2">
                    {['All Posts', 'Strategies', 'Guides', 'Case Studies', 'Company'].map((cat, i) => (
                        <button
                            key={i}
                            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                i === 0
                                    ? 'text-white'
                                    : 'border border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-700'
                            }`}
                            style={i === 0 ? { background: GREEN } : undefined}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* ===== GRID ===== */}
                <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {posts.map((post, index) => (
                        <motion.div
                            key={index}
                            {...reveal}
                            transition={{ duration: 0.5, delay: index * 0.08 }}
                            className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-colors hover:border-gray-300 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
                        >
                            <div className="relative flex h-44 items-center justify-center overflow-hidden border-b border-gray-200 bg-[#F8FAFC] dark:border-gray-800 dark:bg-gray-950">
                                {/* Decorative editorial mark */}
                                <div className="absolute -bottom-10 -right-10 h-32 w-32 rounded-full opacity-60 blur-2xl" style={{ background: '#EFF4FE' }} />
                                <div className="relative flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: GREEN }}>
                                    <BookOpen size={20} strokeWidth={1.8} />
                                </div>
                            </div>
                            <div className="flex flex-1 flex-col p-6">
                                <div className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    <span>{post.category}</span>
                                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
                                </div>
                                <h3 className="font-serif text-xl font-semibold text-gray-900 transition-colors dark:text-white group-hover:text-[#1D4FD8] dark:group-hover:text-[#34D399]">
                                    {post.title}
                                </h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{post.excerpt}</p>
                                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold opacity-0 transition-opacity group-hover:opacity-100" style={{ color: GREEN }}>
                                    Read article <ArrowRight size={15} />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* ===== NEWSLETTER ===== */}
                <motion.div {...reveal} transition={{ duration: 0.55 }} className="mx-auto mt-24 max-w-3xl rounded-2xl border border-gray-200 bg-white p-8 text-center dark:border-gray-800 dark:bg-gray-900 md:p-12">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: GREEN }}>
                        <Download size={20} strokeWidth={1.8} />
                    </div>
                    <h2 className="mt-6 font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">Get the weekly briefing</h2>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">Join 15,000+ recruiters getting the latest AI tips every Tuesday.</p>
                    <div className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:flex-row">
                        <input
                            type="email"
                            placeholder="work@company.com"
                            className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
                        />
                        <button
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-6 py-3 text-base font-semibold text-white transition-colors"
                            style={{ background: GREEN }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}
                        >
                            Subscribe free
                        </button>
                    </div>
                </motion.div>

            </div>
        </div>
    );
}

export default Resources;
